import React from 'react';
import { AlertCircle, RefreshCw, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CameraErrorNoticeProps {
  /** Plain-language failure message produced by the camera lifecycle. */
  message: string;
  onRetry: () => void;
  onUploadInstead: () => void;
  onDismiss: () => void;
  className?: string;
}

/**
 * Camera failure surface for the capture viewport.
 *
 * Capture coaching only renders while the camera is active (it lives in the docked info bar since
 * S3b, and in `FeedbackPanel` before that), so `getUserMedia` failures never reached the user
 * (PLAN B1). This notice renders in the same viewport container while the camera is *inactive*, and
 * always offers a way forward: retry, or fall back to uploading photos (PLAN F3).
 */
const CameraErrorNotice: React.FC<CameraErrorNoticeProps> = ({
  message,
  onRetry,
  onUploadInstead,
  onDismiss,
  className,
}) => (
  <div
    role="alert"
    aria-live="assertive"
    data-testid="camera-error-notice"
    className={cn(
      'absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md flex-col gap-3 rounded-lg border p-4 shadow-lg backdrop-blur-md pointer-events-auto',
      'border-status-deviation/40 bg-status-deviation-surface text-status-deviation',
      'animate-in fade-in slide-in-from-bottom-4 duration-300',
      className,
    )}
  >
    <div className="flex items-start gap-2">
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-snug">{message}</p>
        <p className="mt-1 text-xs opacity-90">
          You can try the camera again, or upload photos from this device instead.
        </p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-status-deviation hover:bg-status-deviation/10"
        aria-label="Dismiss camera error"
        onClick={onDismiss}
      >
        <X className="h-4 w-4" aria-hidden />
      </Button>
    </div>

    <div className="flex flex-wrap gap-2">
      <Button type="button" size="sm" className="min-h-11 flex-1" onClick={onRetry}>
        <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
        Try again
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="min-h-11 flex-1 bg-surface"
        onClick={onUploadInstead}
      >
        <Upload className="mr-2 h-4 w-4" aria-hidden />
        Upload photos instead
      </Button>
    </div>
  </div>
);

export default CameraErrorNotice;
